import { useEffect, useMemo, useRef } from 'react'
import OrderDetailsModal from '../../modals/Cashier/OrderDetailsModal';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentShowTypeOfModal, selectIsFetching, setSelectedId, setTypeOfShowModalType } from '../../../features/modal/modalSlice';
import EmptyData from '../../ui/EmptyData';
import ErrorMessage from '../../ui/ErrorMessage';
import Loading from '../../ui/Loading';
import OrderUpdateModal from '../../modals/Cashier/OrderUpdateModal';
import newOrderSound from '../../../assets/sounds/mixkit-bell-notification.mp3'
import { useMasterData } from '../../../hooks/api/useMasterData';
import OrderItem from '../../common/items/OrderItem';
import { selectOptionName, selectSelectedOptionChapelName } from '../../../features/order/orderSlice';
import { STATUS, STATUS_ORDER } from '../../../constant/constant';
import { useOrderSocket } from '../../../hooks/socket/useOrderSocket';

export default function Orders() {

  const { orders, isErrorOrders, isLoadingOrders } = useMasterData()
  const sortOption = useSelector(selectOptionName);
  const dispatch = useDispatch()
  const selectedOption = useSelector(selectSelectedOptionChapelName)
  const typeOfModalShow = useSelector(selectCurrentShowTypeOfModal);
  const isFetching = useSelector(selectIsFetching)
  const isCurrentlyLoading = isLoadingOrders || isFetching;
  
  const audioRef = useRef(new Audio(newOrderSound));
  const previousIds = useRef<Set<number>>(new Set());

  useEffect(() => {
      const currentIds = new Set(orders.map(order => order.orderID));

      const hasNewOrder = orders.some(
          order => !previousIds.current.has(order.orderID)
      );

      if (previousIds.current.size > 0 && hasNewOrder) {
          audioRef.current.currentTime = 0;

          audioRef.current.play().catch(() => {});
      }

      previousIds.current = currentIds;
  }, [orders]);


  const filteredOrders = useMemo(() => {
      return orders
            .filter((ord) => {
              const matchesChapel =
                  selectedOption === STATUS.all.name
                      ? [STATUS.pending.name, STATUS.brewing.name, STATUS.forPickUp.name].includes(ord.orderStatus)
                      : ord.chapels?.some(
                            (chapel) => chapel.chapelName === selectedOption
                        );

              if (!matchesChapel) {
                  return false;
              }

              if (
                  sortOption !== STATUS.all.name &&
                  ord.orderStatus !== sortOption
              ) {
                  return false;
              }

              return true;
          })
          .sort((a, b) => {
              const statusComparison =
                  STATUS_ORDER.indexOf(a.orderStatus) -
                  STATUS_ORDER.indexOf(b.orderStatus);

              if (statusComparison !== 0) {
                  return statusComparison;
              }

              return b.orderID - a.orderID;
          });
  }, [orders, selectedOption, sortOption]);

  const handleViewOrderDetails = (orderId: number) => {
    dispatch(setSelectedId(orderId));
    dispatch(setTypeOfShowModalType('viewOrderDetails'))
  }

  const handleUpdateOrder = (orderId: number) => {
    dispatch(setSelectedId(orderId));
    dispatch(setTypeOfShowModalType('updateOrder'))
  }

  useOrderSocket()

  return (
    <div className='overflow-hidden rounded'>
      <div className='p-2 h-full rounded overflow-y-auto'>
        {isCurrentlyLoading ? (
            <Loading message="Loading" /> 
        ) : isErrorOrders ? (
          <ErrorMessage message="Unable to load order. Please try again later or refresh the page." />
        ) : filteredOrders.length === 0 ? ( 
            <EmptyData message="No order available." /> 
        ) : ( 
          <div className='grid w-full grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-4 overflow-hidden'>
            {filteredOrders.map((order) => (
              <OrderItem
                key={order.orderID}
                order={order}
                handleViewOrderDetails={handleViewOrderDetails}
                handleUpdateOrder={handleUpdateOrder}
              />
            ))}
          </div>
        )}
        {typeOfModalShow === 'viewOrderDetails' && (
            <OrderDetailsModal />
        )}
        {typeOfModalShow === 'updateOrder' && (
            <OrderUpdateModal />
        )}
      </div>
    </div>
  )
}
